/**
 * ContentForm 컴포넌트
 * 레거시/다국어 입력 모드 전환이 가능한 콘텐츠 편집 폼
 * 요구사항 1.1, 1.2, 1.3, 1.4, 1.5 구현
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Form, Input, Switch, Card, Divider, Typography, Alert, Space } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { I18nInput } from './I18nInput';
import { I18nFormFields as _I18nFormFields } from './I18nFormFields'; // Task 8에서 사용 예정
import { ContentFormData, I18nFieldData, InputMode, ContentData } from '../../types/i18n/i18nTypes';
import {
  convertLegacyToI18n,
  convertI18nToLegacy as _convertI18nToLegacy, // Task 7에서 사용 예정
  createEmptyI18nField,
  cloneI18nField,
} from '../../services/i18n/i18nService';
import { I18N_CONFIG } from '../../../config/i18nConfig';
import './ContentForm.css';

const { Text, Title } = Typography;
const { TextArea } = Input;

export interface ContentFormProps {
  /** 편집할 콘텐츠 데이터 */
  content?: ContentData;
  /** 데이터 변경 핸들러 */
  onChange?: (data: ContentFormData) => void;
  /** 초기 입력 모드 */
  initialMode?: InputMode;
  /** 폼 인스턴스 (Ant Design Form) */
  form?: any;
  /** 비활성화 여부 */
  disabled?: boolean;
  /** 모드 전환 비활성화 여부 */
  disableModeSwitch?: boolean;
  /** 추가 필드 렌더링 함수 */
  renderAdditionalFields?: () => React.ReactNode;
}

/**
 * ContentForm 컴포넌트
 * 레거시 필드와 다국어 필드 간 전환 가능한 콘텐츠 편집 폼을 제공합니다.
 */
export const ContentForm: React.FC<ContentFormProps> = ({
  content,
  onChange,
  initialMode = 'legacy',
  form,
  disabled = false,
  disableModeSwitch = false,
  renderAdditionalFields,
}) => {
  // 현재 입력 모드 상태
  const [inputMode, setInputMode] = useState<InputMode>(initialMode);

  // 폼 데이터 상태
  const [formData, setFormData] = useState<ContentFormData>(() => {
    if (content) {
      return convertLegacyToI18n(content);
    }
    return {
      title: '',
      title_en: '',
      synopsis: '',
      synopsis_en: '',
      seo_title: '',
      seo_synopsis: '',
      seo_keyword: '',
      i18n_title: createEmptyI18nField(),
      i18n_synopsis: createEmptyI18nField(),
      i18n_seo_title: createEmptyI18nField(),
      i18n_seo_synopsis: createEmptyI18nField(),
      i18n_seo_keyword: createEmptyI18nField(),
    };
  });

  // 동기화 상태 추적
  const [lastSyncMode, setLastSyncMode] = useState<InputMode>(inputMode);

  // 초기 데이터 로드 시 폼 데이터 설정
  useEffect(() => {
    if (content) {
      const convertedData = convertLegacyToI18n(content);
      setFormData(convertedData);

      // Ant Design Form에 값 설정
      if (form) {
        form.setFieldsValue({
          title: convertedData.title,
          title_en: convertedData.title_en,
          synopsis: convertedData.synopsis,
          synopsis_en: convertedData.synopsis_en,
          seo_title: convertedData.seo_title,
          seo_synopsis: convertedData.seo_synopsis,
          seo_keyword: convertedData.seo_keyword,
        });
      }
    }
  }, [content, form]);

  // 폼 데이터 변경 시 부모 컴포넌트에 알림
  useEffect(() => {
    if (onChange) {
      onChange(formData);
    }
  }, [formData, onChange]);

  // 입력 모드 전환 핸들러
  const handleModeChange = useCallback(
    (checked: boolean) => {
      const newMode: InputMode = checked ? 'i18n' : 'legacy';

      // 모드 전환 시 데이터 동기화
      let updatedFormData = { ...formData };

      if (newMode === 'i18n' && lastSyncMode === 'legacy') {
        // 레거시 → i18n 모드: 레거시 필드 값을 i18n 필드로 동기화
        updatedFormData = {
          ...formData,
          i18n_title: {
            ...formData.i18n_title,
            ko: formData.title || formData.i18n_title.ko,
            en: formData.title_en || formData.i18n_title.en,
          },
          i18n_synopsis: {
            ...formData.i18n_synopsis,
            ko: formData.synopsis || formData.i18n_synopsis.ko,
            en: formData.synopsis_en || formData.i18n_synopsis.en,
          },
          i18n_seo_title: {
            ...formData.i18n_seo_title,
            ko: formData.seo_title || formData.i18n_seo_title.ko,
          },
          i18n_seo_synopsis: {
            ...formData.i18n_seo_synopsis,
            ko: formData.seo_synopsis || formData.i18n_seo_synopsis.ko,
          },
          i18n_seo_keyword: {
            ...formData.i18n_seo_keyword,
            ko: formData.seo_keyword || formData.i18n_seo_keyword.ko,
          },
        };
      } else if (newMode === 'legacy' && lastSyncMode === 'i18n') {
        // i18n → 레거시 모드: i18n 필드 값을 레거시 필드로 동기화
        updatedFormData = {
          ...formData,
          title: formData.i18n_title.ko || formData.title,
          title_en: formData.i18n_title.en || formData.title_en,
          synopsis: formData.i18n_synopsis.ko || formData.synopsis,
          synopsis_en: formData.i18n_synopsis.en || formData.synopsis_en,
          seo_title: formData.i18n_seo_title.ko || formData.seo_title,
          seo_synopsis: formData.i18n_seo_synopsis.ko || formData.seo_synopsis,
          seo_keyword: formData.i18n_seo_keyword.ko || formData.seo_keyword,
        };

        // Ant Design Form 값 업데이트
        if (form) {
          form.setFieldsValue({
            title: updatedFormData.title,
            title_en: updatedFormData.title_en,
            synopsis: updatedFormData.synopsis,
            synopsis_en: updatedFormData.synopsis_en,
            seo_title: updatedFormData.seo_title,
            seo_synopsis: updatedFormData.seo_synopsis,
            seo_keyword: updatedFormData.seo_keyword,
          });
        }
      }

      setFormData(updatedFormData);
      setInputMode(newMode);
      setLastSyncMode(newMode);
    },
    [formData, lastSyncMode, form]
  );

  // 레거시 필드 변경 핸들러
  const handleLegacyFieldChange = useCallback(
    (fieldName: string, value: string) => {
      const updatedFormData = { ...formData, [fieldName]: value };

      // 실시간 동기화: 레거시 필드 → i18n 필드
      if (fieldName === 'title') {
        updatedFormData.i18n_title = { ...formData.i18n_title, ko: value };
      } else if (fieldName === 'title_en') {
        updatedFormData.i18n_title = { ...formData.i18n_title, en: value };
      } else if (fieldName === 'synopsis') {
        updatedFormData.i18n_synopsis = { ...formData.i18n_synopsis, ko: value };
      } else if (fieldName === 'synopsis_en') {
        updatedFormData.i18n_synopsis = { ...formData.i18n_synopsis, en: value };
      } else if (fieldName === 'seo_title') {
        updatedFormData.i18n_seo_title = { ...formData.i18n_seo_title, ko: value };
      } else if (fieldName === 'seo_synopsis') {
        updatedFormData.i18n_seo_synopsis = { ...formData.i18n_seo_synopsis, ko: value };
      } else if (fieldName === 'seo_keyword') {
        updatedFormData.i18n_seo_keyword = { ...formData.i18n_seo_keyword, ko: value };
      }

      setFormData(updatedFormData);
    },
    [formData]
  );

  // i18n 필드 변경 핸들러
  const handleI18nFieldChange = useCallback(
    (fieldName: string, value: I18nFieldData) => {
      const updatedFormData = {
        ...formData,
        [`i18n_${fieldName}`]: cloneI18nField(value),
      };

      // 실시간 동기화: i18n 필드 → 레거시 필드
      if (fieldName === 'title') {
        updatedFormData.title = value.ko || formData.title;
        updatedFormData.title_en = value.en || formData.title_en;
      } else if (fieldName === 'synopsis') {
        updatedFormData.synopsis = value.ko || formData.synopsis;
        updatedFormData.synopsis_en = value.en || formData.synopsis_en;
      } else if (fieldName === 'seo_title') {
        updatedFormData.seo_title = value.ko || formData.seo_title;
      } else if (fieldName === 'seo_synopsis') {
        updatedFormData.seo_synopsis = value.ko || formData.seo_synopsis;
      } else if (fieldName === 'seo_keyword') {
        updatedFormData.seo_keyword = value.ko || formData.seo_keyword;
      }

      // Ant Design Form 값 업데이트 (레거시 모드에서도 동기화)
      if (form) {
        const formValues: any = {};
        if (fieldName === 'title') {
          formValues.title = updatedFormData.title;
          formValues.title_en = updatedFormData.title_en;
        } else if (fieldName === 'synopsis') {
          formValues.synopsis = updatedFormData.synopsis;
          formValues.synopsis_en = updatedFormData.synopsis_en;
        } else if (fieldName === 'seo_title') {
          formValues.seo_title = updatedFormData.seo_title;
        } else if (fieldName === 'seo_synopsis') {
          formValues.seo_synopsis = updatedFormData.seo_synopsis;
        } else if (fieldName === 'seo_keyword') {
          formValues.seo_keyword = updatedFormData.seo_keyword;
        }
        form.setFieldsValue(formValues);
      }

      setFormData(updatedFormData);
    },
    [formData, form]
  );

  // 모드별 설명 텍스트
  const modeDescription = useMemo(() => {
    if (inputMode === 'legacy') {
      return {
        title: '레거시 입력 모드',
        description: '기존 방식으로 한국어와 영어 필드를 개별적으로 입력합니다.',
        color: 'blue',
      };
    } else {
      return {
        title: '다국어 입력 모드',
        description: '탭 기반 인터페이스로 여러 언어를 효율적으로 관리합니다.',
        color: 'green',
      };
    }
  }, [inputMode]);

  // i18n 기능 활성화 여부 확인
  const isI18nEnabled = I18N_CONFIG.enableI18nFields;

  return (
    <div className="content-form-wrapper">
      {/* 입력 모드 전환 섹션 */}
      {isI18nEnabled && (
        <Card className="content-form-mode-card" size="small">
          <div className="content-form-mode-header">
            <div className="content-form-mode-info">
              <Title level={5} className="content-form-mode-title">
                {modeDescription.title}
              </Title>
              <Text type="secondary" className="content-form-mode-description">
                {modeDescription.description}
              </Text>
            </div>
            <div className="content-form-mode-switch">
              <Space>
                <Text>레거시</Text>
                <Switch
                  checked={inputMode === 'i18n'}
                  onChange={handleModeChange}
                  disabled={disabled || disableModeSwitch}
                  checkedChildren="다국어"
                  unCheckedChildren="레거시"
                />
                <Text>다국어</Text>
              </Space>
            </div>
          </div>

          {/* 모드별 안내 메시지 */}
          <Alert
            message={
              <Space>
                <InfoCircleOutlined />
                <span>
                  {inputMode === 'legacy'
                    ? '레거시 모드에서는 기존 필드(title, title_en 등)를 사용합니다. 입력한 내용은 자동으로 다국어 필드와 동기화됩니다.'
                    : '다국어 모드에서는 탭 기반으로 여러 언어를 관리할 수 있습니다. 한국어는 필수이며, 다른 언어는 선택사항입니다.'}
                </span>
              </Space>
            }
            type="info"
            showIcon={false}
            className="content-form-mode-alert"
          />
        </Card>
      )}

      {/* 기본 정보 섹션 */}
      <Card title="기본 정보" className="content-form-section">
        {inputMode === 'legacy' ? (
          // 레거시 입력 모드
          <>
            <Form.Item
              label="제목"
              name="title"
              rules={[{ required: true, message: '제목을 입력해주세요!' }]}
            >
              <Input
                placeholder="Enter Korean title"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('title', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="제목 (영어)" name="title_en">
              <Input
                placeholder="Enter English title"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('title_en', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="시놉시스" name="synopsis">
              <TextArea
                rows={4}
                placeholder="Enter Korean synopsis"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('synopsis', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="시놉시스 (영어)" name="synopsis_en">
              <TextArea
                rows={4}
                placeholder="Enter English synopsis"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('synopsis_en', e.target.value)}
              />
            </Form.Item>
          </>
        ) : (
          // 다국어 입력 모드
          <>
            <I18nInput
              label="Title"
              value={formData.i18n_title}
              onChange={value => handleI18nFieldChange('title', value)}
              required={true}
              fieldName="title"
              disabled={disabled}
            />

            <Divider />

            <I18nInput
              label="Synopsis"
              value={formData.i18n_synopsis}
              onChange={value => handleI18nFieldChange('synopsis', value)}
              multiline={true}
              rows={4}
              fieldName="synopsis"
              disabled={disabled}
            />
          </>
        )}
      </Card>

      {/* SEO 정보 섹션 */}
      <Card title="SEO 정보" className="content-form-section">
        {inputMode === 'legacy' ? (
          // 레거시 SEO 필드
          <>
            <Form.Item label="SEO 제목" name="seo_title">
              <Input
                placeholder="Enter SEO title"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('seo_title', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="SEO Description" name="seo_synopsis">
              <TextArea
                rows={3}
                placeholder="Enter SEO description"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('seo_synopsis', e.target.value)}
              />
            </Form.Item>

            <Form.Item label="SEO Keyword" name="seo_keyword">
              <Input
                placeholder="Enter SEO keyword (comma separated)"
                disabled={disabled}
                onChange={e => handleLegacyFieldChange('seo_keyword', e.target.value)}
              />
            </Form.Item>
          </>
        ) : (
          // 다국어 SEO 필드
          <>
            <I18nInput
              label="SEO Title"
              value={formData.i18n_seo_title}
              onChange={value => handleI18nFieldChange('seo_title', value)}
              fieldName="seo_title"
              disabled={disabled}
            />

            <Divider />

            <I18nInput
              label="SEO Description"
              value={formData.i18n_seo_synopsis}
              onChange={value => handleI18nFieldChange('seo_synopsis', value)}
              multiline={true}
              rows={3}
              fieldName="seo_synopsis"
              disabled={disabled}
            />

            <Divider />

            <I18nInput
              label="SEO Keyword"
              value={formData.i18n_seo_keyword}
              onChange={value => handleI18nFieldChange('seo_keyword', value)}
              fieldName="seo_keyword"
              disabled={disabled}
              helperText="Enter keywords separated by commas"
            />
          </>
        )}
      </Card>

      {/* 추가 필드 렌더링 */}
      {renderAdditionalFields && renderAdditionalFields()}
    </div>
  );
};

export default ContentForm;
